import React, { useState, useMemo } from "react"
import PropTypes from "prop-types"

import { toData, toMoney } from "utils/string_utils"
import PlanAspect from "./plan-aspect"
import Button from "../shared/button/text-button"

import "./plan-builder.scss"

const getRangeFromMinMax = (min, max, step) => {
  const output = []

  for (let i = min; i <= max; i += step) {
    output.push(i)
  }

  return output
}

const safeIncrement = (val, range) => (val < range.length - 1 ? val + 1 : val)
const safeDecrement = val => (val > 0 ? val - 1 : val)

const PlanBuilder = ({ data, calcCost, onBuildPlan, benefits }) => {
  // Set the aspects with the defauls provided in the data
  const [upfrontIndex, setUpfrontIndex] = useState(
    (data.upfront.default - data.upfront.min) / 10
  )
  const [periodIndex, setPeriodIndex] = useState(
    data.months.default - data.months.min
  )
  const [dataIndex, setDataIndex] = useState(
    data.data.findIndex(datum => datum.default) || 0
  )

  // Need some function for calculating the price given the aspects and phone
  const upfrontRange = useMemo(() => {
    const rangeValues = getRangeFromMinMax(
      data.upfront.min,
      data.upfront.max,
      10
    )
    return rangeValues.map(val => `£${val}`)
  }, [data.upfront.max, data.upfront.min])

  const periodRange = useMemo(() => {
    const rangeValues = getRangeFromMinMax(data.months.min, data.months.max, 1)
    return rangeValues.map(val => `${val}`)
  }, [data.months.max, data.months.min])

  const dataRange = useMemo(() => {
    return data.data.map(({ value }) => toData(value))
  }, [data.data])

  const cost = calcCost(
    upfrontIndex * 10 + data.upfront.min,
    periodIndex + data.months.min,
    dataIndex
  )

  const handleBuiltPlan = () => {
    // build the plan!
    const pickedUpfront = upfrontIndex * 10 + data.upfront.min
    const pickedDuration = periodIndex + data.months.min
    const pickedData = data.data[dataIndex]

    onBuildPlan(pickedUpfront, pickedDuration, pickedData, cost)
  }

  // Store the values of what the current plan is
  // We can use this for the filtered benefit conditions
  const currentValues = {
    upfrontValue: (upfrontIndex + data.upfront.min) * 10,
    periodValue: periodIndex + data.months.min,
    dataValue: data.data[dataIndex].value
  }

  // Grab the relevent benefits
  // Filtering based on condition if it exists
  // All conditions are shown when [conditionKey] >= [conditionValue]
  const filteredBenefits = benefits
    .map(benefitData => {
      if (!benefitData.condition) {
        return benefitData.benefit
      }

      // It's an object, which means conditions
      return Object.keys(benefitData.condition).every(
        conditionKey =>
          currentValues[conditionKey] >= benefitData.condition[conditionKey]
      )
        ? benefitData.benefit
        : null
    })
    .filter(benefit => benefit !== null)

  return (
    <div className="plan-builder-page">
      <div className="plan-builder-page--plan-builder">
        <h1>Customise your plan</h1>
        <PlanAspect
          subText="upfront"
          range={upfrontRange}
          selectedIndex={upfrontIndex}
          onDecrement={() => setUpfrontIndex(safeDecrement)}
          onIncrement={() =>
            setUpfrontIndex(prev => safeIncrement(prev, upfrontRange))
          }
        />
        <PlanAspect
          subText={periodRange[periodIndex] > 1 ? "months" : "month"}
          range={periodRange}
          selectedIndex={periodIndex}
          onDecrement={() => setPeriodIndex(safeDecrement)}
          onIncrement={() =>
            setPeriodIndex(prev => safeIncrement(prev, periodRange))
          }
        />
        <PlanAspect
          subText="data"
          range={dataRange}
          selectedIndex={dataIndex}
          onDecrement={() => setDataIndex(safeDecrement)}
          onIncrement={() =>
            setDataIndex(prev => safeIncrement(prev, dataRange))
          }
        />
        <div className="plan-builder-page--plan-builder--subtext">
          You&apos;ll also get unlimited minutes and texts.
        </div>
      </div>

      <div className="plan-builder-page--offers">
        <h1>Benefits included</h1>
        <ul>
          {filteredBenefits &&
            filteredBenefits.map(benefit => (
              <li key={benefit}>{benefit.mainText}</li>
            ))}
        </ul>
      </div>

      <div className="plan-builder-page--cost">
        <h2>Total Cost</h2>
        <div className="plan-builder-page--cost--amount">
          <span className="plan-builder-page--cost--amount--price">
            {toMoney(cost)}
          </span>{" "}
          a month
        </div>
      </div>

      <div className="plan-builder-page--choose">
        <Button text="Choose this plan" onClick={handleBuiltPlan} />
      </div>
    </div>
  )
}

PlanBuilder.propTypes = {
  data: PropTypes.object.isRequired,
  calcCost: PropTypes.func.isRequired,
  onBuildPlan: PropTypes.func.isRequired,
  benefits: PropTypes.array
}

export default PlanBuilder
